// ═══════════════════════════════════════════════════════════════
// Intake — Discharge
// Two-panel layout: active individuals (left) + recent discharges (right)
// Multi-step discharge modal with destination, condition, signature
// Ported from Discharge.jsx + DischargeForm.jsx
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, TextInput,
  Textarea, Modal, SimpleGrid, Center, ThemeIcon, Stepper,
  Checkbox, Avatar, Divider,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconSearch, IconAlertTriangle, IconCheck,
  IconArrowLeft, IconArrowRight,
} from '@tabler/icons-react';
import { useIntakeStore } from '../../store/intakeStore';
import { useAuthStore } from '../../store/authStore';
import { TRIAGE_COLORS, DESTINATIONS, CONDITIONS, formatDateTime } from './lib/helpers';

// ── Signature Pad (simple canvas) ─────────────────────────────

function SignaturePad({
  onSign,
  onClear,
}: {
  onSign: (dataUrl: string) => void;
  onClear: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    drawingRef.current = true;
    lastPosRef.current = getPos(e);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawingRef.current || !canvasRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastPosRef.current = pos;
  };

  const endDraw = () => {
    if (drawingRef.current && canvasRef.current) {
      drawingRef.current = false;
      onSign(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    onClear();
  };

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>Signature</Text>
      <div style={{ border: '1px solid var(--mantine-color-gray-4)', borderRadius: 8, overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          style={{ width: '100%', height: 150, cursor: 'crosshair', touchAction: 'none' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>
      <Button variant="subtle" size="xs" onClick={clear}>Clear Signature</Button>
    </Stack>
  );
}

// ══════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════

export default function DischargePage() {
  const { individuals, discharges, loadIndividuals, loadDischarges, createDischarge } = useIntakeStore();
  const { activeIncident, user } = useAuthStore();

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Discharge form state
  const [dischargeStep, setDischargeStep] = useState(0);
  const [destination, setDestination] = useState('');
  const [destDetails, setDestDetails] = useState('');
  const [condition, setCondition] = useState('');
  const [notes, setNotes] = useState('');
  const [, setSignatureData] = useState('');
  const [signatureRefused, setSignatureRefused] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (activeIncident?.id) {
      loadIndividuals(String(activeIncident.id));
      loadDischarges(String(activeIncident.id));
    }
  }, [activeIncident?.id, loadIndividuals, loadDischarges]);

  // ── Derived data ──────────────────────────────────────────

  const incidentActive = useMemo(() => {
    if (!activeIncident) return [];
    return individuals
      .filter(i => String(i.incidentId) === String(activeIncident.id) && i.status !== 'discharged')
      .sort((a, b) => {
        const order: Record<string, number> = { red: 0, yellow: 1, green: 2 };
        return (order[a.triage] ?? 3) - (order[b.triage] ?? 3);
      });
  }, [individuals, activeIncident]);

  const recentDischarges = useMemo(() => {
    if (!activeIncident) return [];
    return discharges
      .filter(d => String(d.incidentId) === String(activeIncident.id))
      .sort((a, b) => (b.dischargedAt || b.datecreated).localeCompare(a.dischargedAt || a.datecreated))
      .slice(0, 20);
  }, [discharges, activeIncident]);

  const filtered = useMemo(() => {
    if (!search) return incidentActive;
    const q = search.toLowerCase();
    return incidentActive.filter(i =>
      (i.firstName || '').toLowerCase().includes(q) ||
      (i.lastName || '').toLowerCase().includes(q) ||
      (i.tagCode || '').toLowerCase().includes(q)
    );
  }, [incidentActive, search]);

  const selected = individuals.find(i => i.id === selectedId) || null;

  // ── Modal helpers ─────────────────────────────────────────

  const resetDischargeForm = () => {
    setDischargeStep(0);
    setDestination('');
    setDestDetails('');
    setCondition('');
    setNotes('');
    setSignatureData('');
    setSignatureRefused(false);
    setSelectedId(null);
  };

  const openDischargeModal = (id: number) => {
    resetDischargeForm();
    setSelectedId(id);
  };

  // ── Submit ────────────────────────────────────────────────

  const handleDischarge = async () => {
    if (!selected || !destination) {
      notifications.show({ title: 'Required', message: 'Select a destination.', color: 'red' });
      return;
    }
    setSubmitting(true);
    try {
      await createDischarge({
        individualId: String(selected.id),
        incidentId: String(activeIncident?.id || ''),
        destination,
        destDetails,
        condition,
        dischargedBy: user?.fullName || '',
        notes: [
          notes,
          signatureRefused ? '[Signature refused]' : '',
        ].filter(Boolean).join('\n'),
      });
      notifications.show({
        title: 'Discharged',
        message: `${selected.firstName} ${selected.lastName} has been discharged.`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      resetDischargeForm();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to discharge.',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── No incident guard ───────────────────────────────────────

  if (!activeIncident) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray">
            <IconAlertTriangle size={30} />
          </ThemeIcon>
          <Title order={4}>No Incident Selected</Title>
          <Text size="sm" c="dimmed">Use the incident selector in the top header bar.</Text>
        </Stack>
      </Center>
    );
  }

  // ── Helper for destination label ────────────────────────────

  const getDestLabel = (key: string) =>
    DESTINATIONS.find(d => d.key === key)?.label || key;

  const getCondLabel = (key: string) =>
    CONDITIONS.find(c => c.key === key)?.label || key;

  // ══════════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════════

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Discharge</Title>
          <Badge size="sm" variant="light">{incidentActive.length} awaiting</Badge>
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {/* ── Left Panel: Awaiting Discharge ─────────────── */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} mb="sm">Awaiting Discharge</Text>
          <TextInput
            placeholder="Search by name or tag..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            mb="sm"
          />
          <Stack gap="xs" style={{ maxHeight: 560, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="lg">No active individuals.</Text>
            ) : (
              filtered.map(ind => (
                <Paper
                  key={ind.id}
                  p="sm"
                  radius="sm"
                  withBorder
                  style={{
                    borderLeft: `3px solid ${TRIAGE_COLORS[ind.triage] || '#6b7280'}`,
                    cursor: 'pointer',
                  }}
                  onClick={() => openDischargeModal(ind.id)}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap">
                      <Avatar
                        src={ind.photoUrl || null}
                        radius="xl"
                        size="sm"
                        color={ind.triage === 'red' ? 'red' : ind.triage === 'yellow' ? 'yellow' : 'green'}
                      >
                        {ind.firstName[0]}{ind.lastName[0]}
                      </Avatar>
                      <div>
                        <Text size="sm" fw={500}>{ind.lastName}, {ind.firstName}</Text>
                        <Group gap="xs">
                          <Badge
                            size="xs"
                            color={ind.triage === 'red' ? 'red' : ind.triage === 'yellow' ? 'yellow' : 'green'}
                          >
                            {ind.triage}
                          </Badge>
                          <Text size="xs" c="dimmed" ff="monospace">{ind.tagCode}</Text>
                        </Group>
                      </div>
                    </Group>
                    <Button
                      size="xs"
                      variant="light"
                      onClick={(e) => { e.stopPropagation(); openDischargeModal(ind.id); }}
                    >
                      Discharge
                    </Button>
                  </Group>
                </Paper>
              ))
            )}
          </Stack>
        </Paper>

        {/* ── Right Panel: Recently Discharged ───────────── */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} mb="sm">Recently Discharged</Text>
          {recentDischarges.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No recent discharges.</Text>
          ) : (
            <Stack gap="xs" style={{ maxHeight: 560, overflowY: 'auto' }}>
              {recentDischarges.map(d => {
                const ind = individuals.find(i => String(i.id) === d.individualId);
                return (
                  <Paper key={d.id} p="sm" radius="sm" withBorder>
                    <Group justify="space-between" wrap="nowrap">
                      <div>
                        <Text size="sm" fw={500}>
                          {ind ? `${ind.lastName}, ${ind.firstName}` : `ID ${d.individualId}`}
                        </Text>
                        <Group gap="xs">
                          <Badge size="xs" variant="light" color="blue">{getDestLabel(d.destination)}</Badge>
                          {d.condition && <Badge size="xs" variant="outline">{getCondLabel(d.condition)}</Badge>}
                        </Group>
                      </div>
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(d.dischargedAt || d.datecreated)}
                      </Text>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* ══════════════════════════════════════════════════════ */}
      {/* Discharge Modal — Multi-step                          */}
      {/* ══════════════════════════════════════════════════════ */}
      <Modal
        opened={!!selected}
        onClose={() => { if (!submitting) resetDischargeForm(); }}
        title={selected ? `Discharge: ${selected.firstName} ${selected.lastName}` : 'Discharge'}
        size="lg"
        centered
        closeOnClickOutside={false}
      >
        {selected && (
          <Stack gap="md">
            {/* Individual summary header */}
            <Group gap="sm">
              <Avatar
                src={selected.photoUrl || null}
                radius="xl"
                size="md"
                color={selected.triage === 'red' ? 'red' : selected.triage === 'yellow' ? 'yellow' : 'green'}
              >
                {selected.firstName[0]}{selected.lastName[0]}
              </Avatar>
              <div>
                <Text fw={500}>{selected.firstName} {selected.lastName}</Text>
                <Group gap="xs">
                  <Badge size="xs" color={selected.triage === 'red' ? 'red' : selected.triage === 'yellow' ? 'yellow' : 'green'}>
                    {selected.triage}
                  </Badge>
                  <Badge size="xs" variant="outline">{selected.tagCode}</Badge>
                </Group>
              </div>
            </Group>

            <Divider />

            <Stepper active={dischargeStep} onStepClick={setDischargeStep} size="sm">
              {/* ── Step 1: Destination ───────────────────── */}
              <Stepper.Step label="Destination" description="Where">
                <Stack gap="md" mt="md">
                  <Text fw={500} size="sm">Select Destination</Text>
                  <SimpleGrid cols={{ base: 2, sm: 4 }}>
                    {DESTINATIONS.map(d => (
                      <Paper
                        key={d.key}
                        p="sm"
                        radius="sm"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          textAlign: 'center',
                          borderColor: destination === d.key ? 'var(--color-primary-700)' : undefined,
                          borderWidth: destination === d.key ? 2 : 1,
                          background: destination === d.key ? 'var(--mantine-color-blue-light)' : undefined,
                        }}
                        onClick={() => setDestination(d.key)}
                      >
                        <Text size="xs" fw={destination === d.key ? 700 : 500}>{d.label}</Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                  <TextInput
                    label="Destination Details"
                    placeholder="Hospital name, family member, shelter address..."
                    value={destDetails}
                    onChange={e => setDestDetails(e.target.value)}
                  />
                </Stack>
              </Stepper.Step>

              {/* ── Step 2: Condition ─────────────────────── */}
              <Stepper.Step label="Condition" description="Status">
                <Stack gap="md" mt="md">
                  <Text fw={500} size="sm">Select Condition at Discharge</Text>
                  <SimpleGrid cols={{ base: 1, sm: 5 }}>
                    {CONDITIONS.map(c => (
                      <Paper
                        key={c.key}
                        p="sm"
                        radius="sm"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          textAlign: 'center',
                          borderColor: condition === c.key ? 'var(--color-primary-700)' : undefined,
                          borderWidth: condition === c.key ? 2 : 1,
                          background: condition === c.key ? 'var(--mantine-color-blue-light)' : undefined,
                        }}
                        onClick={() => setCondition(c.key)}
                      >
                        <Text size="xs" fw={condition === c.key ? 700 : 500}>{c.label}</Text>
                      </Paper>
                    ))}
                  </SimpleGrid>
                  <Textarea
                    label="Discharge Notes"
                    placeholder="Additional notes about this discharge..."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    minRows={3}
                    autosize
                  />
                </Stack>
              </Stepper.Step>

              {/* ── Step 3: Signature & Confirm ───────────── */}
              <Stepper.Completed>
                <Stack gap="md" mt="md">
                  <Title order={5}>Review & Confirm</Title>

                  {/* Summary */}
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <div>
                      <Text size="xs" c="dimmed">Individual</Text>
                      <Text size="sm" fw={500}>{selected.firstName} {selected.lastName} ({selected.tagCode})</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Destination</Text>
                      <Text size="sm" fw={500}>{getDestLabel(destination)}</Text>
                      {destDetails && <Text size="xs" c="dimmed">{destDetails}</Text>}
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Condition</Text>
                      <Text size="sm">{condition ? getCondLabel(condition) : 'Not specified'}</Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Discharged By</Text>
                      <Text size="sm">{user?.fullName || 'Unknown'}</Text>
                    </div>
                    {notes && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <Text size="xs" c="dimmed">Notes</Text>
                        <Text size="sm">{notes}</Text>
                      </div>
                    )}
                  </SimpleGrid>

                  <Divider />

                  {/* Signature */}
                  {!signatureRefused && (
                    <SignaturePad
                      onSign={(dataUrl) => setSignatureData(dataUrl)}
                      onClear={() => setSignatureData('')}
                    />
                  )}
                  <Checkbox
                    label="Signature refused"
                    checked={signatureRefused}
                    onChange={e => {
                      setSignatureRefused(e.currentTarget.checked);
                      if (e.currentTarget.checked) setSignatureData('');
                    }}
                  />

                  <Divider />

                  <Group justify="flex-end">
                    <Button variant="subtle" onClick={() => setDischargeStep(0)}>Edit</Button>
                    <Button
                      leftSection={<IconCheck size={14} />}
                      onClick={handleDischarge}
                      loading={submitting}
                      disabled={!destination}
                      color="green"
                      size="md"
                    >
                      Confirm Discharge
                    </Button>
                  </Group>
                </Stack>
              </Stepper.Completed>
            </Stepper>

            {/* Stepper navigation */}
            {dischargeStep < 2 && (
              <Group justify="space-between" mt="sm">
                <Button
                  variant="subtle"
                  leftSection={<IconArrowLeft size={14} />}
                  onClick={() => setDischargeStep(Math.max(0, dischargeStep - 1))}
                  disabled={dischargeStep === 0}
                >
                  Previous
                </Button>
                <Button
                  rightSection={<IconArrowRight size={14} />}
                  onClick={() => setDischargeStep(dischargeStep + 1)}
                  disabled={dischargeStep === 0 && !destination}
                >
                  {dischargeStep === 1 ? 'Review' : 'Next'}
                </Button>
              </Group>
            )}
          </Stack>
        )}
      </Modal>
    </div>
  );
}
